import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Paper, Avatar, Button, Link, IconButton } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import moment from 'moment';

import WebsiteAvatar from 'src/components/WebsiteAvatar';
import { formatStatusDisplay } from 'src/utils/statusUtils';
import { getStatusColor } from 'src/constants/statusColors';
import DateEditModal from 'src/pages/Reports/components/DateEditModal';
import { hasCustomReportDate } from 'src/utils/queryNotesUtils';
import { getQueryDateForDisplay } from 'src/utils/queryUtils';

const MobileCard = ({
  query,
  isExternal = false,
  onRemoveExternalEffort,
  onEditExternalEffort,
  company,
  showActions = true,
  statusType,
  onDateUpdate,
  dateRanges,
  dateFilter,
  allReports,
  allReportsMutate,
  allowDateEdit = true,
}) => {
  const [dateModalOpen, setDateModalOpen] = useState(false);

  const getStatusText = () => `${formatStatusDisplay(query.status)}${isExternal ? ' (Additional)' : ''}`;

  // Determine which date to display
  // For regular queries: use the same priority order as filterByDateRange
  // For external efforts: check reportDate field directly, fall back to date
  const originalDate = isExternal ? query.date : query.updateTime;
  const displayDate = isExternal ? query.reportDate || originalDate : getQueryDateForDisplay(query);

  const isUsingReportDate = isExternal ? Boolean(query.reportDate) : hasCustomReportDate(query);

  const handleDateClick = () => {
    setDateModalOpen(true);
  };

  const handleDateUpdate = (queryId, newReportDate) => {
    if (onDateUpdate) {
      onDateUpdate(queryId, newReportDate);
    }
  };

  return (
    <Paper
      sx={{
        mb: 2,
        p: 2,
        border: '1px solid #ddd',
        borderRadius: 2,
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {isExternal ? (
          <WebsiteAvatar url={query.outletLink || query.link} outlet={query.outlet} size={40} />
        ) : (
          <Avatar
            src={query.qwotedImage}
            sx={{
              width: 40,
              height: 40,
              fontSize: '1rem',
              fontWeight: 'bold',
              backgroundColor: 'white',
              '& img': {
                objectFit: 'contain',
              },
            }}
          />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {isExternal ? query.outlet : query.mediaOutlet}
          </Typography>
          <Typography
            variant="caption"
            onClick={allowDateEdit ? handleDateClick : undefined}
            sx={{
              color: isUsingReportDate ? 'primary.main' : 'text.secondary',
              fontWeight: isUsingReportDate ? 600 : 400,
              cursor: allowDateEdit ? 'pointer' : 'default',
              ...(allowDateEdit && {
                '&:hover': {
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  padding: '2px 4px',
                },
              }),
            }}
            title={allowDateEdit ? (isUsingReportDate ? 'Custom report date (click to edit)' : 'Click to set custom report date') : undefined}
          >
            {moment(displayDate).format('MMM DD, YYYY')}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ fontWeight: '600', mb: 1, color: 'text.primary' }}>
        {query.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" sx={{ color: `${getStatusColor(query.status)?.paletteKey || 'primary'}.main` }}>
          {getStatusText()}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {isExternal ? (
          <>
            {query.link && (
              <Button component={Link} href={query.link} target="_blank" variant="contained" size="small" sx={{ textTransform: 'none' }}>
                View
              </Button>
            )}
            {onEditExternalEffort && showActions && (
              <IconButton size="small" color="primary" onClick={() => onEditExternalEffort(company, query)} title="Edit additional effort">
                <Edit fontSize="small" />
              </IconButton>
            )}
            {onRemoveExternalEffort && showActions && (
              <IconButton size="small" color="error" onClick={() => onRemoveExternalEffort(query.id)} title="Remove additional effort">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <>
            {showActions && (
              <Button
                component={Link}
                href={`/query-profile/${query.id}`}
                target="_blank"
                variant="contained"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Query
              </Button>
            )}
            {query.mediaURL && (
              <Button component={Link} href={query.mediaURL} target="_blank" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
                Article
              </Button>
            )}
          </>
        )}
      </Box>

      <DateEditModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        effort={query}
        isExternal={isExternal}
        onSuccess={handleDateUpdate}
        company={company}
        dateRanges={dateRanges}
        dateFilter={dateFilter}
        allReports={allReports}
        allReportsMutate={allReportsMutate}
      />
    </Paper>
  );
};

MobileCard.propTypes = {
  query: PropTypes.object.isRequired,
  isExternal: PropTypes.bool,
  onRemoveExternalEffort: PropTypes.func,
  onEditExternalEffort: PropTypes.func,
  company: PropTypes.string,
  showActions: PropTypes.bool,
  statusType: PropTypes.oneOf(['wins', 'inProgress']).isRequired,
  onDateUpdate: PropTypes.func,
  dateRanges: PropTypes.object,
  dateFilter: PropTypes.string,
  allReports: PropTypes.array,
  allReportsMutate: PropTypes.func,
  allowDateEdit: PropTypes.bool,
};

export default MobileCard;
